import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  AdminDashboardTab,
  adminDashboardTabHref,
  getAdminDashboardTabLabel,
  parseAdminDashboardTab,
} from '../../utils/adminDashboardTabs';
import { AdminHeaderActionsSlot } from '../../contexts/AdminHeaderActionsContext';

interface AdminLayoutProps {
  children: React.ReactNode;
  inactiveUserCount?: number;
}

interface NavItemConfig {
  key: AdminDashboardTab;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  inactiveUserCount = 0,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab: AdminDashboardTab = parseAdminDashboardTab(searchParams);
  const [globalSearch, setGlobalSearch] = useState(() => searchParams.get('search') || '');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  React.useEffect(() => {
    if (activeTab === 'tabletop') {
      setGlobalSearch(searchParams.get('search') || '');
    }
  }, [activeTab]);

  // Persistent collapsed state across page navigations & refreshes
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('flashai.admin_sidebar.collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const [tooltip, setTooltip] = useState<{ text: string; top: number } | null>(null);

  const toggleCollapsed = () => {
    setTooltip(null);
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('flashai.admin_sidebar.collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const handleGlobalSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeTab === 'tabletop') {
      navigate(`/admin?tab=tabletop${globalSearch.trim() ? `&search=${encodeURIComponent(globalSearch.trim())}` : ''}`);
      return;
    }
    if (!globalSearch.trim()) return;
    navigate(`/admin?tab=users&search=${encodeURIComponent(globalSearch.trim())}`);
  };

  const currentTabLabel = getAdminDashboardTabLabel(activeTab);

  const userInitials =
    user?.full_name
      ?.split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'SA';

  const isCurrentTab = (tab: AdminDashboardTab) => activeTab === tab;

  // Icon definitions for every admin tab
  const overviewItem: NavItemConfig = {
    key: 'overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
      </svg>
    ),
  };

  const peopleItems: NavItemConfig[] = [
    {
      key: 'users',
      label: 'Users',
      badge: inactiveUserCount > 0 ? inactiveUserCount : undefined,
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
    },
    {
      key: 'insurers',
      label: 'Insurers',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
  ];

  const revenueItems: NavItemConfig[] = [
    {
      key: 'subscriptions',
      label: 'Subscriptions',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      key: 'payments',
      label: 'Payments',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'pricing',
      label: 'Pricing',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      ),
    },
    {
      key: 'demo-requests',
      label: 'Demo Leads',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
    },
  ];

  const preparednessItems: NavItemConfig[] = [
    {
      key: 'bcps',
      label: 'BCPs',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      key: 'training',
      label: 'Academy',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422A12.083 12.083 0 0118 17.5c-.58.31-1.169.59-1.76.84M12 14l-6.16-3.422A12.083 12.083 0 006 17.5c.58.31 1.169.59 1.76.84M12 14v7" />
        </svg>
      ),
    },
    {
      key: 'tabletop',
      label: 'Tabletops',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'crisis-communications',
      label: 'Crises',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      ),
    },
    {
      key: 'templates',
      label: 'Templates',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
        </svg>
      ),
    },
  ];

  const systemItems: NavItemConfig[] = [
    {
      key: 'security',
      label: 'Security',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
    },
  ];

  const renderNavItem = (item: NavItemConfig) => {
    const active = isCurrentTab(item.key);
    const label = item.key === 'crisis-communications' ? 'Crisis Communications' : item.label;
    const tooltipText = `${label}${item.badge !== undefined ? ` (${item.badge})` : ''}`;
    return (
      <Link
        key={item.key}
        to={adminDashboardTabHref(item.key)}
        onMouseEnter={(e) => {
          if (!isCollapsed) return;
          const rect = e.currentTarget.getBoundingClientRect();
          setTooltip({
            text: tooltipText,
            top: rect.top + rect.height / 2,
          });
        }}
        onMouseLeave={() => setTooltip(null)}
        className={`group relative flex items-center ${
          isCollapsed ? 'justify-center p-2.5' : 'justify-between px-3 py-2'
        } rounded-lg text-sm font-medium transition-colors ${
          active
            ? 'bg-slate-100 text-slate-900 font-semibold border-l-2 border-rose-600 pl-2.5'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
        }`}
      >
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
          <span className={active ? 'text-slate-900' : 'text-slate-500 group-hover:text-slate-800'}>
            {item.icon}
          </span>
          {!isCollapsed && <span className="truncate">{item.label}</span>}
        </div>

        {/* Badge */}
        {item.badge !== undefined && (
          isCollapsed ? (
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-600 ring-2 ring-white"></span>
          ) : (
            <span className="px-1.5 py-0.5 text-xs font-bold text-rose-700 bg-rose-100 rounded-full">
              {item.badge}
            </span>
          )
        )}
      </Link>
    );
  };

  return (
    <div className="flex h-screen bg-slate-50 text-slate-800 antialiased overflow-hidden">
      {/* Left Sidebar */}
      <aside
        className={`bg-white border-r border-slate-200 flex flex-col flex-shrink-0 select-none z-20 transition-all duration-300 ease-in-out ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Brand / Logo & Collapse Toggle */}
        <div className="relative p-4 pb-3 border-b border-slate-100 flex items-center min-h-[61px]">
          <Link
            to="/admin"
            className={`flex items-center overflow-hidden ${
              isCollapsed ? 'mx-auto justify-center' : ''
            }`}
          >
            <div
              className={`overflow-hidden flex-shrink-0 flex items-center transition-[width] duration-300 ease-in-out ${
                isCollapsed ? 'w-[34px] justify-start' : 'w-[145px]'
              }`}
            >
              <img
                src="/flashailogo.png"
                alt="FLASH"
                className="h-9 w-auto max-w-none object-contain object-left flex-shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          </Link>

          {/* Collapse/Expand Toggle Button */}
          <button
            type="button"
            onClick={toggleCollapsed}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors z-30 cursor-pointer"
          >
            {isCollapsed ? (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>

        {/* Staging Admin Badge */}
        {!isCollapsed && (
          <div className="px-4 py-2 border-b border-slate-50">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FFF7ED] text-[#C2410C] border border-[#FED7AA]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C]"></span>
              Staging · Admin
            </span>
          </div>
        )}

        {/* Navigation list */}
        <nav
          onScroll={() => setTooltip(null)}
          className="flex-1 px-3 py-3 space-y-4 overflow-y-auto overflow-x-hidden [scrollbar-width:thin]"
        >
          {/* Top Level: Overview */}
          <div className="space-y-1">
            {renderNavItem(overviewItem)}
          </div>

          {/* PEOPLE section */}
          <div>
            {!isCollapsed ? (
              <div className="px-3 text-[10px] font-bold tracking-wider text-slate-400 uppercase mb-1">
                People
              </div>
            ) : (
              <div className="my-2 border-t border-slate-100" />
            )}
            <div className="space-y-1">{peopleItems.map(renderNavItem)}</div>
          </div>

          {/* REVENUE section */}
          <div>
            {!isCollapsed ? (
              <div className="px-3 text-[10px] font-bold tracking-wider text-slate-400 uppercase mb-1">
                Revenue
              </div>
            ) : (
              <div className="my-2 border-t border-slate-100" />
            )}
            <div className="space-y-1">{revenueItems.map(renderNavItem)}</div>
          </div>

          {/* PREPAREDNESS section */}
          <div>
            {!isCollapsed ? (
              <div className="px-3 text-[10px] font-bold tracking-wider text-slate-400 uppercase mb-1">
                Preparedness
              </div>
            ) : (
              <div className="my-2 border-t border-slate-100" />
            )}
            <div className="space-y-1">{preparednessItems.map(renderNavItem)}</div>
          </div>

          {/* SYSTEM section */}
          <div>
            {!isCollapsed ? (
              <div className="px-3 text-[10px] font-bold tracking-wider text-slate-400 uppercase mb-1">
                System
              </div>
            ) : (
              <div className="my-2 border-t border-slate-100" />
            )}
            <div className="space-y-1">{systemItems.map(renderNavItem)}</div>
          </div>
        </nav>

        {/* User Card at bottom of sidebar */}
        <div className="p-3 border-t border-slate-200 relative">
          <button
            type="button"
            onClick={() => setIsUserMenuOpen((prev) => !prev)}
            onMouseEnter={(e) => {
              if (!isCollapsed) return;
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltip({
                text: `${user?.full_name || 'System Administrator'} (${user?.is_superuser ? 'Super admin' : 'Admin'})`,
                top: rect.top + rect.height / 2,
              });
            }}
            onMouseLeave={() => setTooltip(null)}
            className={`w-full flex items-center ${
              isCollapsed ? 'justify-center p-1.5' : 'space-x-3 p-2'
            } rounded-lg hover:bg-slate-100 transition-colors text-left`}
          >
            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
              {userInitials}
            </div>
            {!isCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 truncate">
                    {user?.full_name || 'System Administrator'}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {user?.is_superuser ? 'Super admin' : 'Admin'}
                  </div>
                </div>
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
              </>
            )}
          </button>

          {isUserMenuOpen && (
            <div
              className={`absolute bottom-16 ${
                isCollapsed ? 'left-14 w-48' : 'left-3 right-3'
              } bg-white rounded-lg shadow-xl border border-slate-200 py-1 text-xs z-50`}
            >
              <Link
                to="/dashboard"
                className="block px-3.5 py-2 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Switch to Workspace
              </Link>
              <Link
                to="/profile"
                className="block px-3.5 py-2 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Profile & MFA
              </Link>
              <Link
                to="/analytics-dashboard"
                className="block px-3.5 py-2 text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Analytics Dashboard
              </Link>
              <button
                type="button"
                onClick={() => {
                  setIsUserMenuOpen(false);
                  logout();
                }}
                className="w-full text-left px-3.5 py-2 text-rose-600 hover:bg-rose-50 border-t border-slate-100"
              >
                Sign out
              </button>
            </div>
          )}
        </div>

        {/* Floating Tooltip when collapsed */}
        {isCollapsed && tooltip && (
          <div
            style={{ top: tooltip.top }}
            className="fixed left-[88px] -translate-y-1/2 z-50 pointer-events-none flex items-center"
          >
            <div className="relative px-2.5 py-1 text-xs font-semibold text-white bg-slate-900 rounded-md shadow-xl whitespace-nowrap">
              <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-900 rotate-45" />
              {tooltip.text}
            </div>
          </div>
        )}
      </aside>

      {/* Main Canvas Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header Bar */}
        <header className="h-14 bg-white border-b border-slate-200 px-6 sm:px-8 flex items-center justify-between flex-shrink-0 z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center space-x-2 text-sm text-slate-500 font-medium">
            <Link to="/admin" className="hover:text-slate-800 transition-colors">
              Admin
            </Link>
            <span className="text-slate-300">/</span>
            <span className="font-semibold text-slate-900">{currentTabLabel}</span>
          </div>

          {/* Actions & Search */}
          <div className="flex items-center space-x-3">
            <AdminHeaderActionsSlot />

            {/* Search Bar */}
            <form onSubmit={handleGlobalSearch} className="relative w-64 max-w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={activeTab === 'tabletop' ? 'Search exercise, client or email' : 'Search users, orgs, payments...'}
                value={globalSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setGlobalSearch(val);
                  if (activeTab === 'tabletop') {
                    navigate(`/admin?tab=tabletop${val.trim() ? `&search=${encodeURIComponent(val.trim())}` : ''}`, { replace: true });
                  }
                }}
                className="block w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg placeholder-slate-400 text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:bg-white transition-all"
              />
            </form>
          </div>
        </header>

        {/* Scrollable Content View */}
        <main className="flex-1 overflow-y-auto bg-[#F8FAFC]">
          <div className="w-full px-6 sm:px-8 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
